import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StyledTableCell,
  StyledTableRow,
  useStyles,
} from "components/profiles/ProfileStyles";
import { screenStyles } from "../../Styles";
import {
  translatePersonalTransactionsTypes,
  translateLSBDTransactionsTypes,
  TransactionsTypes,
} from "../../ScreenUtils";
import Actions from "./TransactionsTableActions";
import { FormatDateToFront } from "utils/dates/FormatDateToFront";
import { valueToMoneyFloat } from "utils/general/valueToMoneyFloat";
import { moneyMask } from "utils/masks/moneyMask";
import { Box } from "@material-ui/core";
import { dateToBR } from "utils/dates/DateToBRr";
import { normalizeStr } from "utils/general/strings";

function desc(a, b, orderBy, isLSBD, isManager) {
  if (a[orderBy] === null || a[orderBy] === undefined) {
    a[orderBy] = "";
  }
  if (b[orderBy] === null || b[orderBy] === undefined) {
    b[orderBy] = "";
  }

  if (orderBy === "date" || orderBy === "value") {
    if (
      b[orderBy] < a[orderBy] ||
      b["date"] < a["date"] ||
      b["createdAt"] < a["createdAt"]
    ) {
      return -1;
    }
    if (
      b[orderBy] > a[orderBy] ||
      b["date"] > a["date"] ||
      b["createdAt"] > a["createdAt"]
    ) {
      return 1;
    }

    return 0;
  }

  if (orderBy === "type") {
    var translate = isLSBD
      ? translateLSBDTransactionsTypes
      : translatePersonalTransactionsTypes;

    if (translate(b[orderBy]) < translate(a[orderBy])) {
      return -1;
    }
    if (translate(b[orderBy]) > translate(a[orderBy])) {
      return 1;
    }

    if (b["createdAt"] > a["createdAt"] || b["date"] > a["date"]) {
      return 1;
    }
    if (b["createdAt"] < a["createdAt"] || b["date"] < a["date"]) {
      return -1;
    }

    return 0;
  }

  if (orderBy === "user") {
    let attributeA = a["operator"] ? "operator" : "user";
    let attributeB = b["operator"] ? "operator" : "user";
    if (
      normalizeStr(b[attributeB].shortname.toString()) <
      normalizeStr(a[attributeA].shortname.toString())
    ) {
      return -1;
    }
    if (
      normalizeStr(b[attributeB].shortname.toString()) >
      normalizeStr(a[attributeA].shortname.toString())
    ) {
      return 1;
    }

    if (b["date"] > a["date"] || b["createdAt"] > a["createdAt"]) {
      return 1;
    }
    if (b["date"] < a["date"] || b["createdAt"] < a["createdAt"]) {
      return -1;
    }

    return 0;
  }

  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy, isLSBD) {
  return order === "desc"
    ? (a, b) => desc(a, b, orderBy, isLSBD)
    : (a, b) => -desc(a, b, orderBy, isLSBD);
}

function createContent(rows, headers, order, orderBy, isLSBD) {
  const rowsCopy = [];
  var obj = {};
  for (var i = 0; i < rows.length; i++) {
    for (var j = 0; j < headers.length; j++) {
      obj[headers[j]] = rows[i][headers[j]];
    }
    rowsCopy.push(obj);
    obj = {};
  }
  return stableSort(rowsCopy, getSorting(order, orderBy, isLSBD));
}

const TransactionsTable = props => {
  const styles = screenStyles();
  const classes = useStyles();
  const [t] = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState({});
  const [rowsLength, setRowsLength] = useState(0);
  const [headers, setHeaders] = useState([]);
  const [mainField, setMainField] = useState("");
  const [visibleHeaders, setVisibleHeaders] = useState([]);
  const [sortedFields, setSortedFields] = useState([]);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("shortname");

  React.useEffect(() => {
    setHeaders(props.headers);
    setRows(props.rows);
    setRowsLength(props.rows.length);
    setMainField(props.mainField);
    setVisibleHeaders(props.visibleHeaders);
    setSortedFields(props.sortedFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.headers, props.mainField, props.rows, props.visibleHeaders]);

  function handlePageChange(event, newPage) {
    setPage(newPage);
  }
  const handleRowsPerPageChange = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === sortedFields[property] && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(sortedFields[property]);
  };

  const UsersTableHead = props => {
    const { order, orderBy, onRequestSort, isLSBD } = props;
    const [t] = useTranslation();
    const paginationTop = action => e => {
      var selector = "[aria-label^='" + action + "']";
      document.querySelector(selector).click();
      e.preventDefault();
    };
    const createSortHandler = property => event => {
      onRequestSort(event, property);
    };

    let actionIndex = isLSBD ? 4 : 3;

    return (
      <TableHead>
        <TableRow>
          {visibleHeaders.map((header, index) => (
            <StyledTableCell
              key={index}
              size="small"
              align="left"
              sortDirection={orderBy === sortedFields[index] ? order : false}
            >
              {index !== actionIndex ? (
                <TableSortLabel
                  active={orderBy === sortedFields[index]}
                  direction={order}
                  onClick={createSortHandler(index)}
                >
                  {header.toUpperCase()}
                  {orderBy === sortedFields[index] ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              ) : null}
            </StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  UsersTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
  };

  React.useEffect(() => {
    if (props.toFirstPage) {
      document.getElementById("toFirstPage").click();
    }
  }, [props.rows, props.toFirstPage]);

  const toFirstPage = () => {
    setPage(0);
  };

  return (
    <Paper className={classes.root}>
      <div className={classes.tableWrapper}>
        <Table className={classes.table} size="small">
          {rows.length > 0 && (
            <UsersTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              isLSBD={props.isLSBD}
            />
          )}
          <TableBody>
            {createContent(rows, headers, order, orderBy, props.isLSBD)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(row => (
                <StyledTableRow key={row.id}>
                  <TableCell size="small" width={props.isLSBD ? "15%" : "60%"}>
                    {row["date"]
                      ? dateToBR(FormatDateToFront(row["date"]))
                      : "-"}
                  </TableCell>

                  {props.isLSBD && (
                    <TableCell size="small" width={"45%"}>
                      {row["externalContributorUser"]
                        ? row["externalContributorUser"]
                        : row["user"]
                        ? row["user"].collaboratorName
                        : row["operator"]
                        ? row["operator"].collaboratorName
                        : "-"}
                    </TableCell>
                  )}

                  <TableCell size="small">
                    {row["type"] && !props.isLSBD
                      ? translatePersonalTransactionsTypes(row["type"])
                      : row["type"]
                      ? translateLSBDTransactionsTypes(
                          row["type"],
                          row["source"]
                        )
                      : "-"}
                  </TableCell>

                  <TableCell size="small">
                    {row["value"] ? (
                      <Box
                        color={
                          row["type"] === TransactionsTypes.CREDIT ||
                          row["type"] === TransactionsTypes.CANCELLED_DEBIT
                            ? "#27AE60"
                            : row["type"] === TransactionsTypes.DEBIT ||
                              row["type"] === TransactionsTypes.CANCELLED_CREDIT
                            ? "#F3222F"
                            : null
                        }
                        fontWeight="700"
                      >
                        {`${
                          row["type"] === TransactionsTypes.CREDIT ||
                          row["type"] === TransactionsTypes.CANCELLED_DEBIT
                            ? "+"
                            : "-"
                        } R$
                                                ${moneyMask(
                                                  valueToMoneyFloat(
                                                    Math.abs(row["value"])
                                                  )
                                                )}
                                                `}
                      </Box>
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  <TableCell size="small" align="right">
                    <div
                      style={{
                        marginTop: "auto",
                        marginBottom: "auto",
                        display: "flex",
                        flexWrap: "nowrap ",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Actions
                        item={row}
                        name={row.shortname}
                        permission={props.permission}
                        isLSBD={props.isLSBD}
                        isManager={props.isManager}
                      />
                    </div>
                  </TableCell>
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
        <button hidden id="toFirstPage" onClick={toFirstPage}></button>
        {rows.length > 0 && (
          <TableFooter style={{ float: "right" }}>
            <TablePagination
              labelRowsPerPage={"Resultados por página"}
              count={rowsLength}
              rowsPerPage={rowsPerPage}
              page={page}
              component="div"
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </TableFooter>
        )}
      </div>
    </Paper>
  );
};

TransactionsTable.propTypes = {
  rows: PropTypes.array.isRequired,
  headers: PropTypes.array.isRequired,
  visibleHeaders: PropTypes.array.isRequired,
  mainField: PropTypes.string.isRequired,
};

export default TransactionsTable;
